import gql from 'graphql-tag'
import { FetchResult } from '@apollo/client'

import { PAYMENT_METHOD } from 'lib/core/resources/constants'
import { apolloClient } from 'lib/core/services/magento/client'

import { Mes } from './vendor/mes'
import { useCheckout } from '../../../modules/checkout/hooks/useCheckout'

export interface AuthorizeCard {
  creditCardData: {
    expMonth: string
    expYear: string
    number: string
  }
}

export interface CreateAuthorizeCard {
  getMesConfig: GetMesConfig
  lockCheckout: () => void
  unlockCheckout: () => void
}

export interface GetMesConfigOutput {
  profile_id: string
  profile_key: string
  sandbox: boolean
}

export interface SetPaymentMethodParams {
  token: string
  last4: string
  ccType: string
  cvv: string
  saveCard: boolean
  ccExpMonth?: string
  ccExpYear?: string
}

type SetPaymentMethodOnCartMutation = (params: SetPaymentMethodParams) => Promise<FetchResult<any>>

type GetMesConfig = () => Promise<FetchResult<GetMesConfigOutput>>

const createAuthorizeCard = (deps: CreateAuthorizeCard) => async (params: AuthorizeCard): Promise<string> => {
  const { expMonth, expYear, number } = params.creditCardData
  deps.lockCheckout()
  try {
    const { data: { getMesConfig: mesConfig } }: any = await deps.getMesConfig()
    const environment = mesConfig.sandbox ? 'sandbox' : 'production'
    const token: string = await new Promise((resolve, reject) => {
      Mes.tokenize(number, ('0' + expMonth).slice(-2) + expYear, (result: any) => {
        if (result.code === 0) {
          return resolve(result.token)
        }
        const getErrorString = (code: number) => {
          switch (code) {
            case 1: return 'Your browser is not compatible with the payment security enforced by this website. Please upgrade or use the latest version of any modern web browser.'
            case 2: return 'Invalid Credit Card Number.'
            case 3: return 'Invalid expiry date or expired card.'
            case 4: return 'There was an error processing the request with the gateway.'
            case 5: return 'An error occurred due to the browser you are using. Please upgrade or use the latest version of any modern web browser.'
            case 6: return 'There was an error processing the request. Please try again or contact the administrator.'
            case 7: return 'This website must be secured with SSL to proceed. Please contact the website administrator.'
          }
        }
        return reject(new Error(`Error code ${result.code}: ${getErrorString(result.code)}`))
      }, environment)
    })
    deps.unlockCheckout()
    return token
  } catch (error) {
    deps.unlockCheckout()
    throw error
  }
}

export const getMesConfigQuery = gql`
  query {
    getMesConfig {
      profile_id
      profile_key
      sandbox
    }
  }
`

export const useMerchantE = () => {
  const { cartId, lockCheckout, unlockCheckout } = useCheckout()

  const getMesConfig: GetMesConfig = async () => apolloClient.query({
    fetchPolicy: 'no-cache',
    query: getMesConfigQuery
  })

  const setPaymentMethodOnCart: SetPaymentMethodOnCartMutation = async ({ token, last4, ccType, cvv, saveCard, ccExpMonth, ccExpYear }) => {
    const query = `
      mutation {
        setPaymentMethodOnCart (
          input: {
            cart_id: "${cartId}",
            payment_method: {
              code: "${PAYMENT_METHOD.MERCHANTE}"
              merchant_e_solutions: {
                cc_token: "${token}"
                cc_last_4: "${last4}"
                cc_type: "${ccType}"
                cc_cid: "${cvv}"
                ${!!ccExpMonth && `cc_exp_month: "${ccExpMonth}"`}
                ${!!ccExpYear && `cc_exp_year: "${ccExpYear}"`}
                save_card: "${saveCard}"
              }
            }
          }
        ) {
          cart {
            selected_payment_method {
              code
              title
            }
          }
        }
      }
    `
    return apolloClient.mutate({ mutation: gql`${query}` })
  }

  return {
    authorizeCard: createAuthorizeCard({
      getMesConfig,
      lockCheckout,
      unlockCheckout
    }),
    setPaymentMethodOnCart
  }
}
